<script lang="ts">
  import {
    acceptBuild,
    getBuild,
    promoteBuild,
    type BuildDetail,
    type DiffRow,
  } from "../lib/api";
  import DiffModal from "./DiffModal.svelte";

  let { id }: { id: string } = $props();
  let data = $state<BuildDetail | null>(null);
  let error = $state<string | null>(null);
  let busyKey = $state<string | null>(null);
  let stateFilter = $state<"all" | "changed" | "added" | "removed" | "unchanged">("changed");
  let modalIndex = $state<number | null>(null);

  // Pixel-delta threshold filter — pure reviewer-side squelch, doesn't
  // mutate the underlying diff state in Turso. Diffs whose pixel_delta_pct
  // is below the threshold are hidden from the visible[] derivation, so
  // small rendering jitter that CI flagged as "changed" can be muted
  // without re-running diff. Threshold is in PERCENT (0–5 range) for
  // human ergonomics; the underlying diff field is a unit fraction.
  let threshold = $state(0);
  // Parse the threshold off the URL hash so links share filter state.
  // e.g. #/builds/<id>?threshold=0.5
  $effect(() => {
    const hash = window.location.hash || "#/";
    const queryStart = hash.indexOf("?");
    if (queryStart < 0) return;
    const params = new URLSearchParams(hash.slice(queryStart + 1));
    const t = Number(params.get("threshold"));
    if (Number.isFinite(t) && t >= 0 && t <= 5) {
      threshold = t;
    }
  });
  function persistThreshold() {
    const hash = window.location.hash || "#/";
    const [base, query] = hash.split("?");
    const params = new URLSearchParams(query ?? "");
    if (threshold > 0) {
      params.set("threshold", String(threshold));
    } else {
      params.delete("threshold");
    }
    const qs = params.toString();
    window.history.replaceState(null, "", base + (qs ? "?" + qs : ""));
  }

  async function load() {
    error = null;
    try {
      data = await getBuild(id);
    } catch (e) {
      error = String(e);
    }
  }
  $effect(() => {
    load();
  });

  async function accept(story?: string) {
    busyKey = story ?? "__all__";
    try {
      await acceptBuild(id, story);
      await load();
    } catch (e) {
      error = String(e);
    } finally {
      busyKey = null;
    }
  }

  async function promote(branch: string) {
    busyKey = "__promote__";
    try {
      const r = await promoteBuild(id, branch);
      alert(`promoted ${r.promoted} shot(s) to ${branch}`);
      await load();
    } catch (e) {
      error = String(e);
    } finally {
      busyKey = null;
    }
  }

  // visible = diffs of the chosen state, AND above the pixel-delta
  // threshold. Added/removed diffs have pixel_delta_pct = 0 so they're
  // never squelched — only changed diffs can fall below threshold.
  const visible = $derived.by(() => {
    if (!data) return [] as DiffRow[];
    let rows = data.diffs;
    if (stateFilter !== "all") {
      rows = rows.filter((d) => d.state === stateFilter);
    }
    if (threshold > 0) {
      const cutoff = threshold / 100;
      rows = rows.filter((d) => {
        if (d.state === "added" || d.state === "removed") return true;
        return d.pixel_delta_pct >= cutoff;
      });
    }
    return rows;
  });

  // Count of "changed" diffs that are CURRENTLY squelched below the
  // threshold — surfaced in the UI so reviewers know the slider is
  // doing something even when nothing's visibly different.
  const squelched = $derived.by(() => {
    if (!data || threshold <= 0) return 0;
    const cutoff = threshold / 100;
    return data.diffs.filter(
      (d) => d.state === "changed" && d.pixel_delta_pct < cutoff,
    ).length;
  });

  function openModal(i: number) {
    modalIndex = i;
  }
  function closeModal() {
    modalIndex = null;
  }
</script>

<section>
  <a class="back" href="#/">← back</a>
  {#if error}
    <p class="err">{error}</p>
  {/if}
  {#if data}
    {@const b = data.build}
    <h2>
      <code>{b.id.slice(0, 28)}…</code>
    </h2>
    <p class="meta">
      <span><b>repo</b> {b.repo}</span>
      <span><b>branch</b> {b.branch}</span>
      <span><b>sha</b> <code>{b.sha.slice(0, 12)}</code></span>
      {#if b.pr_number}<span><b>pr</b> #{b.pr_number}</span>{/if}
      <span><b>status</b> {b.status}</span>
    </p>
    <div class="actions">
      <button
        onclick={() => accept()}
        disabled={busyKey !== null}
      >accept all pending changes</button>
      <button
        onclick={() => promote("main")}
        disabled={busyKey !== null}
      >promote → main</button>
      <button
        onclick={() => promote(b.branch)}
        disabled={busyKey !== null}
      >promote → {b.branch}</button>
    </div>

    <div class="filters">
      {#each ["all", "changed", "added", "removed", "unchanged"] as s (s)}
        <button
          class:active={stateFilter === s}
          onclick={() => (stateFilter = s as typeof stateFilter)}
        >{s}</button>
      {/each}
    </div>

    <div class="threshold">
      <label for="threshold-slider">
        squelch &lt; <strong>{threshold.toFixed(2)}%</strong> pixel delta
      </label>
      <input
        id="threshold-slider"
        type="range"
        min="0"
        max="5"
        step="0.05"
        bind:value={threshold}
        oninput={persistThreshold}
      />
      {#if squelched > 0}
        <span class="squelched">{squelched} changed hidden</span>
      {/if}
      {#if threshold > 0}
        <button class="reset" onclick={() => { threshold = 0; persistThreshold(); }}>reset</button>
      {/if}
    </div>

    {#if visible.length === 0}
      <p class="muted">no diffs in this state.</p>
    {:else}
      <div class="grid">
        {#each visible as d, i (d.id)}
          <article
            class={`card state-${d.state}`}
            tabindex="0"
            role="button"
            aria-label={`Open diff ${d.story_id} ${d.viewport} ${d.theme}`}
            onclick={() => openModal(i)}
            onkeydown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                openModal(i);
              }
            }}
          >
            <header>
              <span class={`pill ${d.state}`}>{d.state}</span>
              <span class="title">{d.story_title || d.story_id}</span>
              <span class="vp">{d.viewport} · {d.theme}</span>
              {#if d.state !== "unchanged"}
                <button
                  class="accept"
                  disabled={d.review_state === "accepted" || busyKey !== null}
                  onclick={(e) => { e.stopPropagation(); accept(d.id); }}
                >{d.review_state === "accepted" ? "accepted" : "accept"}</button>
              {/if}
              <button
                class="expand"
                title="open fullscreen (Enter)"
                onclick={(e) => { e.stopPropagation(); openModal(i); }}
                aria-label="open fullscreen"
              >⤢</button>
            </header>
            <div class="trio">
              <figure>
                <figcaption>baseline</figcaption>
                {#if d.baseline_url}
                  <img src={d.baseline_url} alt="baseline" loading="lazy" />
                {:else}
                  <div class="empty">—</div>
                {/if}
              </figure>
              <figure>
                <figcaption>current</figcaption>
                {#if d.current_url}
                  <img src={d.current_url} alt="current" loading="lazy" />
                {:else}
                  <div class="empty">—</div>
                {/if}
              </figure>
              <figure>
                <figcaption>diff</figcaption>
                {#if d.diff_url}
                  <img src={d.diff_url} alt="diff" loading="lazy" />
                {:else}
                  <div class="empty">—</div>
                {/if}
              </figure>
            </div>
            {#if d.pixel_delta > 0}
              <footer class="muted">
                Δ {d.pixel_delta.toLocaleString()} px ({(d.pixel_delta_pct * 100).toFixed(2)}%)
              </footer>
            {/if}
          </article>
        {/each}
      </div>
    {/if}
  {:else if !error}
    <p class="muted">loading…</p>
  {/if}
</section>

{#if modalIndex !== null && visible.length > 0}
  <DiffModal
    diffs={visible}
    index={modalIndex}
    onClose={closeModal}
    onNavigate={(i) => (modalIndex = i)}
    onAccept={(story) => accept(story)}
    busyKey={busyKey}
  />
{/if}

<style>
  .back {
    display: inline-block;
    margin-bottom: 12px;
    font-size: 13px;
  }
  h2 {
    margin: 0 0 4px;
    font-weight: 500;
  }
  .meta {
    display: flex;
    gap: 16px;
    flex-wrap: wrap;
    font-size: 13px;
    color: #9aa1ad;
    margin: 4px 0 16px;
  }
  .meta b {
    color: #6b7180;
    font-weight: 500;
    margin-right: 4px;
  }
  .actions {
    display: flex;
    gap: 8px;
    margin-bottom: 16px;
  }
  .actions button {
    background: #1c2230;
    border: 1px solid #2a3142;
    border-radius: 6px;
    padding: 8px 14px;
    color: #e6e8ee;
    cursor: pointer;
  }
  .actions button:hover:not(:disabled) {
    border-color: #7df9c0;
  }
  .filters {
    display: flex;
    gap: 6px;
    margin: 12px 0;
  }
  .filters button {
    background: transparent;
    border: 1px solid #2a3142;
    border-radius: 999px;
    padding: 4px 12px;
    color: #9aa1ad;
    font-size: 12px;
    cursor: pointer;
  }
  .filters button.active {
    background: #1c2230;
    color: #e6e8ee;
    border-color: #7df9c0;
  }
  .threshold {
    display: flex;
    align-items: center;
    gap: 12px;
    margin: 4px 0 16px;
    font-size: 12px;
    color: #9aa1ad;
  }
  .threshold label {
    white-space: nowrap;
  }
  .threshold strong {
    color: #7df9c0;
    font-weight: 500;
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  }
  .threshold input[type="range"] {
    flex: 1;
    max-width: 280px;
    accent-color: #7df9c0;
  }
  .threshold .squelched {
    color: #f7b955;
    font-size: 11px;
  }
  .threshold .reset {
    background: transparent;
    border: 1px solid #2a3142;
    border-radius: 6px;
    color: #9aa1ad;
    padding: 2px 10px;
    font-size: 11px;
    cursor: pointer;
  }
  .threshold .reset:hover {
    color: #e6e8ee;
    border-color: #7df9c0;
  }
  .grid {
    display: grid;
    grid-template-columns: 1fr;
    gap: 16px;
  }
  .card {
    background: #131722;
    border: 1px solid #1c2230;
    border-radius: 10px;
    padding: 12px;
    cursor: pointer;
    transition: border-color 0.12s;
  }
  .card:hover, .card:focus {
    border-color: #7df9c0;
    outline: none;
  }
  .card .expand {
    background: transparent;
    border: 1px solid #2a3142;
    color: #9aa1ad;
    border-radius: 6px;
    width: 28px;
    height: 28px;
    cursor: pointer;
    font-size: 14px;
    margin-left: 4px;
  }
  .card .expand:hover { color: #7df9c0; border-color: #7df9c0; }
  .card.state-changed { border-left: 3px solid #f7b955; }
  .card.state-added   { border-left: 3px solid #7df9c0; }
  .card.state-removed { border-left: 3px solid #ff7c7c; }
  .card header {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 10px;
  }
  .card header .title {
    font-weight: 500;
  }
  .card header .vp {
    color: #6b7180;
    font-size: 12px;
  }
  .card header .accept {
    margin-left: auto;
    background: #1c2230;
    border: 1px solid #2a3142;
    border-radius: 6px;
    padding: 4px 12px;
    color: #7df9c0;
    cursor: pointer;
    font-size: 12px;
  }
  .card header .accept:disabled {
    color: #6b7180;
    border-color: #1c2230;
    cursor: default;
  }
  .trio {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    gap: 8px;
  }
  .trio figure {
    margin: 0;
    background: #0f1218;
    border-radius: 6px;
    overflow: hidden;
  }
  .trio figcaption {
    font-size: 11px;
    color: #6b7180;
    padding: 4px 8px;
  }
  .trio img,
  .trio .empty {
    width: 100%;
    display: block;
  }
  .trio .empty {
    height: 200px;
    display: grid;
    place-items: center;
    color: #6b7180;
    background:
      repeating-linear-gradient(
        45deg,
        #1c2230,
        #1c2230 6px,
        #131722 6px,
        #131722 12px
      );
  }
  .card footer {
    margin-top: 8px;
    font-size: 12px;
  }
  .muted { color: #6b7180; }
  .err { color: #ff7c7c; }
  .pill {
    display: inline-block;
    padding: 2px 8px;
    border-radius: 999px;
    font-size: 11px;
    background: #1c2230;
  }
  .pill.changed { color: #f7b955; }
  .pill.added   { color: #7df9c0; }
  .pill.removed { color: #ff7c7c; }
  code {
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    font-size: 13px;
  }
</style>
